import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dateDiff, dateFormat, convertToPercent, formatCurrencyRound, truncate, getTileKey, getFromSession, updateSessionObject, deepCopy } from "../../app/util";
import { updateAppState } from "../../actions/appstate";
import "./table-reports.css";
import "./table-groupiq-reports.css";
import { toast } from 'react-toastify';
import DataService from "../../services/data.service";
import { Box, CircularProgress, Tooltip } from '@mui/material/';


function TableGroupIQReports({ dataSource }) {

    const dispatch = useDispatch();
    const { app, auth } = useSelector((state) => state);
    const { app_state, ui_state } = app.current_state;
    const { currency_symbol } = ui_state;
    const { profile } = auth;
    const savedUserPreferences = profile?.column_preferences;
    const [isSaving, setIsSaving] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    // Data source to be looped
    const formattedDataSource = useMemo(() => {
        const data = dataSource[0].data.map((item) => {
            let total_rooms = 0;
            item.params_called_with.dates.forEach((dateEntry) => {
                total_rooms += dateEntry.rooms.reduce((sum, room) => sum + room.rooms, 0);
            });

            const total_nights = dateDiff(
                item.data.primary.overall.departure_date,
                item.data.primary.overall.arrival_date
            );
            const rooms_per_day = total_nights > 0 ? Math.round(total_rooms / total_nights) : 0;

            const arrivalYear = new Date(item.data.primary.overall.arrival_date).getFullYear();
            const departureYear = new Date(item.data.primary.overall.departure_date).getFullYear();
            const dateRange =
                arrivalYear === departureYear
                    ? `${dateFormat(item.data.primary.overall.arrival_date, "MMM DD")} - ${dateFormat(item.data.primary.overall.departure_date, "MMM DD, YYYY")}`
                    : `${dateFormat(item.data.primary.overall.arrival_date, "MMM DD, YYYY")} - ${dateFormat(item.data.primary.overall.departure_date, "MMM DD, YYYY")}`;

            return {
                proposal_id: item.group_quote_id,
                date_range: dateRange,
                name: item.group_name,
                type: item.type,
                wish_rate: formatCurrencyRound(currency_symbol, item.data.primary.overall.wish.rate),
                // walk_rate: formatCurrencyRound(currency_symbol, item.data.primary.overall.walk.rate), // LRV-1083 - Remove Walk Rate
                custom_rate: item.data.primary.overall.custom ? formatCurrencyRound(currency_symbol, item.data.primary.overall.custom.rate) : "-",
                created_on: dateFormat(item.generated_at, "MMM DD, YYYY"),
                comission: convertToPercent(item.params_called_with.comission_percent) + "%",
                comp_room: item.params_called_with.comp_rooms.rooms,
                created_by: item.user_name ? item.user_name : "-",
                additional_notes: item.additional_notes,
                //expiry_date: dateFormat(expiry, "MMM DD, YYYY"),
                total_nights,
                rooms_per_day,
                arrival_date: new Date(item.data.primary.overall.arrival_date) // Add arrival_date for sorting
            };
        });

        // Apply sorting
        if (sortConfig.key) {
            data.sort((a, b) => {
                let valueA = a[sortConfig.key];
                let valueB = b[sortConfig.key];
                if (sortConfig.key === "created_on") {
                    valueA = new Date(valueA);
                    valueB = new Date(valueB);
                }
                if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
                if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
                return 0;
            });
        } else {
            data.sort((a, b) => a.arrival_date - b.arrival_date);
        }

        return data;
    }, [dataSource, sortConfig]);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };


    // Columns to be looped
    const [columns, setColumns] = useState([
        { id: 'proposal_id', label: 'Proposal ID', order: 1 },
        { id: 'date_range', label: 'Date Range', order: 2 },
        { id: 'name', label: 'Name', order: 3 },
        { id: 'type', label: 'Type', order: 4 },
        { id: 'total_nights', label: 'Total Nights', order: 5 },
        { id: 'rooms_per_day', label: 'Rooms/Day', order: 6 },
        { id: 'wish_rate', label: 'Wish Rate', order: 7 },
        // { id: 'walk_rate', label: 'Walk Rate', order: 8 }, // LRV-1083 - Remove Walk Rate
        { id: 'custom_rate', label: 'Custom Rate', order: 8 },
        { id: 'created_on', label: 'Created on', order: 9 },
        // { id: 'expiry_date', label: 'Expiry Date', order: 10 },
        { id: 'comission', label: 'Commission', order: 10 },
        { id: 'comp_room', label: 'Comp. Rooms', order: 11 },
        { id: 'created_by', label: 'Created By', order: 12 },
    ])

    const [draggedIndex, setDraggedIndex] = useState(null);
    const baselineColumnsRef = useRef(null);

    // Restore column order from session storage on mount (priority: session > savedPreferences > default)
    useEffect(() => {
        const tileName = getTileKey(ui_state.active_tiles[0], app_state.workspace_controls, app_state.property_id);
        let restoredCols = null;

        // 2nd priority: savedUserPreferences from profile
        if (savedUserPreferences && savedUserPreferences[tileName] && savedUserPreferences[tileName]['tableConfig'] && savedUserPreferences[tileName]['tableConfig']['columns']) {
            restoredCols = savedUserPreferences[tileName]['tableConfig']['columns'];
        }

        // 1st priority: session storage overrides profile preferences
        const sessionData = JSON.parse(getFromSession('user_temp_preferences'));
        if (sessionData && sessionData[tileName] && sessionData[tileName]['tableConfig'] && sessionData[tileName]['tableConfig']['columns']) {
            restoredCols = sessionData[tileName]['tableConfig']['columns'];
        }

        if (restoredCols) {
            setColumns(prev => {
                const prevMap = new Map(prev.map(col => [col.id, col]));
                const restored = restoredCols
                    .filter(saved => prevMap.has(saved.id))
                    .map((saved, i) => ({ ...prevMap.get(saved.id), order: i + 1 }));
                const newCols = restored.length > 0 ? restored : prev;
                baselineColumnsRef.current = deepCopy(newCols);
                return newCols;
            });
        } else {
            baselineColumnsRef.current = deepCopy(columns);
        }
    }, []);

    const handleDragStart = (index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (event, dropIndex) => {
        event.preventDefault();
        const updatedColumns = [...columns].sort((a, b) => a.order - b.order);
        const [draggedColumn] = updatedColumns.splice(draggedIndex, 1);
        updatedColumns.splice(dropIndex, 0, draggedColumn);
        updatedColumns.forEach((col, i) => { col.order = i + 1; });
        setColumns(updatedColumns);
        setDraggedIndex(null);

        // lrv-3129 saving updated columns in session storage.
        let tempData = '';
        const activeTile = ui_state.active_tiles[0];
        const tile = getTileKey(activeTile, app_state.workspace_controls, app_state.property_id);

        if (savedUserPreferences && Object.keys(savedUserPreferences).length > 0) {
            tempData = { ...savedUserPreferences };
            tempData[tile] = { ...(tempData[tile] || {}) };
            tempData[tile].tableConfig = { ...(tempData[tile].tableConfig || {}) };
            tempData[tile].tableConfig.columns = updatedColumns;
            updateSessionObject('user_temp_preferences', tempData);
        } else {
            tempData = {};
            tempData[tile] = {};
            tempData[tile]['tableConfig'] = {};
            tempData[tile]['tableConfig']['columns'] = updatedColumns;
            updateSessionObject('user_temp_preferences', tempData);
        }
        const referenceColumns = savedUserPreferences?.[tile]?.tableConfig?.columns || baselineColumnsRef.current;
        const isChanged = referenceColumns
            ? referenceColumns.some(ref => {
                const curr = updatedColumns.find(c => c.id === ref.id);
                return !curr || curr.order !== ref.order;
            })
            : true;
        dispatch(updateAppState('columnSettingsChanged', { ui_state: { columnSettingsChanged: isChanged } }));
    };

    const formattedProposalID = (id) => {
        const firstPart = id.split('-')[0];
        return firstPart;
    }

    // Function to download report
    const handleDownloadReport = async (group_quote_id) => {
        const payload = {
            group_quote_id: group_quote_id
        }
        console.log("payload", payload)
        setIsSaving(true);
        try {
            const response = await DataService.update({
                api: "rms/properties/download-group-quote-v2/" + app_state.property_id + "/",
                request: payload
            });
            if (response.status === "success") {
                console.log("Report downloaded successfully", response);
                // Create a download link and trigger it programmatically
                const link = document.createElement('a');
                link.href = response.exportUrl;
                link.download = response.file; // filename coming from response
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setIsSaving(false);
                toast.dismiss();
                toast.success("Report downloaded successfully", {
                    position: "bottom-right"
                });
            }
            else {
                console.log("Report not downloaded")
                setIsSaving(false);
                toast.dismiss();
                toast.error("Report couldn't be download. Please try again.", {
                    position: "bottom-right"
                });
            }
        }
        catch (err) {
            console.error('An error occurred', err);
            setIsSaving(false);
            toast.dismiss();
            toast.error("Report couldn't be download. Please try again.", {
                position: "bottom-right"
            });
        }
    }

    const displayData = (str, colId) => {
        if (str !== undefined) {
            if (colId !== 'name') {
                return str;
            }
            if (colId === 'name' && str.length <= 20) {
                return str;
            }
            return (
                <Tooltip title={str} arrow>
                    {truncate(str, 20)}
                </Tooltip>
            );
        }
    };

    return (
        <Paper sx={{ width: "100%" }} elevation={0} className="group-iq-report-container">
            {formattedDataSource.length === 0 ?
                <div className="group-iq-report-empty-screen">
                    <div className="empty-screen-container">
                        <svg width="66" height="86" viewBox="0 0 66 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M64.5892 16.648V80.0599C64.5892 82.5992 62.5491 84.66 60.0354 84.66H17.4125C14.8988 84.66 12.8588 82.5992 12.8588 80.0599V74.1121H48.5874C51.101 74.1121 53.1411 72.0512 53.1411 69.512V12.048H60.0354C62.5491 12.048 64.5892 14.1088 64.5892 16.648Z" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M53.1411 6.10007V69.512C53.1411 72.0512 51.101 74.112 48.5873 74.112H5.96438C3.45072 74.112 1.41064 72.0512 1.41064 69.512V15.475H10.1675C12.6811 15.475 14.7212 13.4142 14.7212 10.8749V1.5H48.5873C51.101 1.5 53.1411 3.56083 53.1411 6.10007Z" fill="white" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M14.7212 1.5L1.41064 15.475" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M20.6093 8.48743H46.0327" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M18.6056 14.7988H46.0327" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8.0636 21.1146H46.0326" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M15.3268 27.4259H46.0326" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8.0636 33.7372H46.0326" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M8.0636 40.0486H46.0326" stroke="#67C8F5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M47.6583 62.9247L42.2531 68.385" stroke="#FFC9C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M42.2577 62.9247L47.663 68.385" stroke="#FFC9C9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <div className="empty-text">No Proposals are Available for These Stay Dates.</div>
                    </div>
                </div>
                :
                <TableContainer component={Paper} className="table-scrollable">
                    <Table sx={{ minWidth: 650 }} aria-label="simple table" className="react-dynamic-table">
                        <TableHead className="table-head">
                            <TableRow className="header_row top_header">
                                {[...columns].sort((a, b) => a.order - b.order).map((column, index) =>
                                    column.id !== "proposal_id" ? (
                                        <TableCell
                                            className={`primary-header-cell header_col ${column.id !== "proposal_id" ? "header-secondary" : ""}`}
                                            key={column.id}
                                            draggable
                                            onDragStart={() => handleDragStart(index)}
                                            onDragOver={handleDragOver}
                                            onDrop={(event) => handleDrop(event, index)}
                                            sx={{ padding: "2px 16px !important" }}
                                        >
                                            {["name", "type", "created_on"].includes(column.id) ?
                                                <Box className='col-flex'>
                                                    <Box className="sorting-icons">
                                                        <svg
                                                            className={`order-arrow up-arrow ${sortConfig.key === column.id && sortConfig.direction === "asc" ? "active" : ""}`}
                                                            onClick={() => handleSort(column.id, 'asc')}
                                                            xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#fff" viewBox="0 0 256 256"><path d="M215.39,163.06A8,8,0,0,1,208,168H48a8,8,0,0,1-5.66-13.66l80-80a8,8,0,0,1,11.32,0l80,80A8,8,0,0,1,215.39,163.06Z"></path>
                                                        </svg>
                                                        <svg
                                                            className={`order-arrow down-arrow ${sortConfig.key === column.id && sortConfig.direction === "desc" ? "active" : ""}`}
                                                            onClick={() => handleSort(column.id, 'desc')}
                                                            xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="#fff" viewBox="0 0 256 256"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,48,88H208a8,8,0,0,1,5.66,13.66Z"></path>
                                                        </svg>
                                                    </Box>
                                                    <Box>{column.label}</Box>
                                                </Box>
                                                :
                                                <Box>{column.label}</Box>
                                            }
                                        </TableCell>
                                    ) : (
                                        <TableCell
                                            key={column.id}
                                            className={`primary-header-cell header_col col-sticky`}
                                            sx={{ padding: "2px 16px !important" }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    )
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody className="table-body">
                            {formattedDataSource.map((row, index) => (
                                <TableRow key={index} className="table-row">
                                    {[...columns].sort((a, b) => a.order - b.order).map((column) => (
                                        <TableCell
                                            className={`data-cell ${column.id === "proposal_id" ? "col-sticky left-align" : ""} ${column.id === "date_range" || column.id === "name" || column.id === "type" || column.id === "created_by" ? "left-align" : ""}`}
                                            key={column.id}
                                        >
                                            {column.id === "proposal_id" ? (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                                    <div
                                                        className="download-icon"
                                                        style={isSaving ? { pointerEvents: "none" } : {}}
                                                        onClick={() => handleDownloadReport(row.proposal_id)}
                                                    >
                                                        {isSaving ? (
                                                            <CircularProgress color="inherit" size={14} />
                                                        ) : (
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="16"
                                                                height="16"
                                                                fill="#606060"
                                                                viewBox="0 0 256 256"
                                                            >
                                                                <path d="M224,144v64a8,8,0,0,1-8,8H40a8,8,0,0,1-8-8V144a8,8,0,0,1,16,0v56H208V144a8,8,0,0,1,16,0Zm-101.66,5.66a8,8,0,0,0,11.32,0l40-40a8,8,0,0,0-11.32-11.32L136,124.69V32a8,8,0,0,0-16,0v92.69L93.66,98.34a8,8,0,0,0-11.32,11.32Z"></path>
                                                            </svg>
                                                        )}
                                                    </div>
                                                    <div
                                                        style={isSaving ? { pointerEvents: "none" } : {}}
                                                        onClick={() => handleDownloadReport(row.proposal_id)}
                                                    >
                                                        {formattedProposalID(row.proposal_id)}
                                                    </div>
                                                </Box>
                                            ) : (
                                                displayData(row[column.id], column.id)
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Paper>
    );
}

export default TableGroupIQReports;